import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { sb, setAuth0IdTokenGetter } from '../lib/supabase';
import { setAuth0AccessTokenGetter } from '../lib/ai';
import { resolveSupabaseIdToken, clearAuthRecoveryFlag } from '../lib/authToken';
import { clearReturnTo } from '../lib/postLoginRedirect';
import { getActiveOrganisationId, setActiveOrganisationId } from '../lib/activeOrg';
import { useDemoPersonaOptional } from './DemoPersonaContext';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { isLoading, isAuthenticated, user, logout, getIdTokenClaims, getAccessTokenSilently } = useAuth0();
  const [profile, setProfile] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);

  const linkPendingInvite = useCallback(async (email, fullName) => {
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL?.replace(/\/$/, '');
    const supabaseAnon = process.env.REACT_APP_SUPABASE_ANON_KEY;
    if (!audience || !supabaseUrl || !supabaseAnon || !email) return null;

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience },
      });
      const res = await fetch(`${supabaseUrl}/functions/v1/auth0-provision-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: supabaseAnon,
        },
        body: JSON.stringify({
          mode: 'check',
          email,
          full_name: fullName || undefined,
        }),
      });
      if (!res.ok) return null;
      const data = await res.json().catch(() => ({}));
      return data?.profile && typeof data.profile === 'object' ? data.profile : null;
    } catch {
      return null;
    }
  }, [getAccessTokenSilently]);

  // One Auth0 identity can now hold a profile row in more than one organisation
  // (multi-org membership). Fetch all of them, then pick the "active" one —
  // whichever org is remembered in localStorage, falling back to the most
  // recently created membership — and expose the rest as `memberships` so the
  // org switcher has something to show.
  const fetchProfile = useCallback(async (authSubject, email, fullName) => {
    if (!authSubject) {
      setProfile(null);
      setMemberships([]);
      return null;
    }

    const { data, error } = await sb
      .from('profiles')
      .select('*, organisations:organisation_id(name, is_suspended, suspended_reason)')
      .eq('auth_subject', authSubject)
      .order('created_at', { ascending: false })
      .limit(25);

    if (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[auth] profile fetch failed:', error.message);
      }
      setProfile(null);
      setMemberships([]);
      return null;
    }

    const rows = Array.isArray(data) ? data : [];

    if (!rows.length && email) {
      const linked = await linkPendingInvite(email, fullName);
      setProfile(linked || null);
      setMemberships(linked ? [linked] : []);
      if (linked?.organisation_id) setActiveOrganisationId(linked.organisation_id);
      return linked || null;
    }

    const preferredOrgId = getActiveOrganisationId();
    const active = (preferredOrgId && rows.find((r) => r.organisation_id === preferredOrgId)) || rows[0] || null;

    setMemberships(rows);
    setProfile(active);
    if (active?.organisation_id) setActiveOrganisationId(active.organisation_id);
    return active;
  }, [linkPendingInvite]);

  // Switch which organisation the user is acting as. A full navigation (rather
  // than in-place state swap) keeps every org-scoped screen/subscription simple
  // and correct — switching orgs is rare enough that a reload is a fine price.
  const switchOrganisation = useCallback((organisationId) => {
    if (!organisationId || organisationId === profile?.organisation_id) return;
    const target = memberships.find((m) => m.organisation_id === organisationId);
    if (!target) return;
    setActiveOrganisationId(organisationId);
    window.location.assign('/home');
  }, [memberships, profile?.organisation_id]);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated || !user?.sub) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    let cancelled = false;
    setProfileLoading(true);
    fetchProfile(user.sub, user.email, user.name).finally(() => {
      if (!cancelled) setProfileLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoading, isAuthenticated, user?.sub, user?.email, user?.name, fetchProfile]);

  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

  const refreshSupabaseSession = useCallback(async () => {
    if (!isAuthenticated) return false;
    const token = await resolveSupabaseIdToken({
      getIdTokenClaims,
      getAccessTokenSilently,
      audience,
    });
    return Boolean(token);
  }, [isAuthenticated, getIdTokenClaims, getAccessTokenSilently, audience]);

  useLayoutEffect(() => {
    setAuth0IdTokenGetter(async () => {
      const token = await resolveSupabaseIdToken({
        getIdTokenClaims,
        getAccessTokenSilently,
        audience,
      });
      return token ? { __raw: token } : undefined;
    });
    return () => setAuth0IdTokenGetter(null);
  }, [getAccessTokenSilently, getIdTokenClaims, audience]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return undefined;
    clearAuthRecoveryFlag();
    const onFocus = () => {
      refreshSupabaseSession().catch(() => undefined);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') onFocus();
    });
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [isLoading, isAuthenticated, refreshSupabaseSession]);

  useEffect(() => {
    setAuth0AccessTokenGetter((opts) => getAccessTokenSilently(opts));
    return () => setAuth0AccessTokenGetter(null);
  }, [getAccessTokenSilently]);

  const loading = isLoading || profileLoading;

  const applyProfilePatch = (patch) => {
    if (!patch || typeof patch !== 'object') return;
    setProfile((prev) => ({ ...(prev || {}), ...patch }));
  };

  // Platform-staff actions (deactivate a user, suspend a company) both work by flipping a flag
  // rather than deleting anything — this is where that flag actually takes effect for the user.
  const isDeactivated = Boolean(profile?.is_deactivated);
  const isOrgSuspended = Boolean(profile?.organisations?.is_suspended);
  const isBlocked = isDeactivated || isOrgSuspended;
  const blockedReason = isDeactivated
    ? (profile?.deactivated_reason || 'Your account has been deactivated.')
    : (profile?.organisations?.suspended_reason || "Your company's access has been suspended.");

  const value = useMemo(
    () => ({
      loading,
      user,
      profile,
      memberships,
      switchOrganisation,
      isAuthenticated,
      isBlocked,
      blockedReason,
      signOut: () => {
        clearReturnTo();
        const returnTo = window.location.origin;
        const openUrl = (url) => {
          try {
            const u = new URL(url);
            u.searchParams.set('returnTo', returnTo);
            window.location.assign(u.toString());
          } catch {
            window.location.replace(returnTo);
          }
        };
        try {
          const p = logout({ logoutParams: { returnTo, federated: true }, openUrl });
          if (p && typeof p.then === 'function') {
            p.catch(() => {
              window.location.replace(returnTo);
            });
          }
        } catch {
          window.location.replace(returnTo);
        }
      },
      refreshProfile: () => fetchProfile(user?.sub, user?.email, user?.name),
      refreshSupabaseSession,
      applyProfilePatch,
    }),
    [loading, user, profile, memberships, switchOrganisation, isAuthenticated, logout, fetchProfile, refreshSupabaseSession, isBlocked, blockedReason]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useSupabaseAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useSupabaseAuth must be used within AuthProvider');
  }
  return ctx;
}

/** Org-level platform role — admin surfaces, org-wide settings. */
export function useAccess() {
  const { profile } = useSupabaseAuth();
  return useMemo(() => {
    const role = profile?.platform_role;
    const isAdmin = role === 'admin';
    const isViewer = role === 'viewer';
    const isMember = role === 'member' || role === 'consultant';
    const canWrite = Boolean(profile) && !isViewer;
    return { isAdmin, isViewer, isMember, canWrite };
  }, [profile]);
}

function accessFromRole(role, loading) {
  return {
    role,
    loading,
    isAdmin: role === 'admin',
    isViewer: role === 'viewer',
    isMember: role === 'member',
    canWrite: role === 'admin' || role === 'member',
    hasAccess: role != null,
  };
}

/** Resolved programme role via DB (includes org-admin bypass). */
export function useProgrammeAccess(programmeId) {
  const demoCtx = useDemoPersonaOptional();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(Boolean(programmeId));

  useEffect(() => {
    if (!programmeId) {
      setRole(null);
      setLoading(false);
      return;
    }
    if (
      demoCtx?.isDemoView
      && demoCtx.activeProgrammeId === programmeId
      && demoCtx.demoAccessRole
    ) {
      setRole(demoCtx.demoAccessRole);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    sb.rpc('effective_programme_role', { p_programme_id: programmeId })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error && process.env.NODE_ENV !== 'production') {
          console.warn('[access] effective_programme_role failed:', error.message);
        }
        setRole(data ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [programmeId, demoCtx?.isDemoView, demoCtx?.activeProgrammeId, demoCtx?.demoAccessRole]);

  return useMemo(() => accessFromRole(role, loading), [role, loading]);
}

/** Resolved project role (inherits from parent programme). */
export function useProjectAccess(projectId) {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(Boolean(projectId));

  useEffect(() => {
    if (!projectId) {
      setRole(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    sb.rpc('effective_project_role', { p_project_id: projectId })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error && process.env.NODE_ENV !== 'production') {
          console.warn('[access] effective_project_role failed:', error.message);
        }
        setRole(data ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId]);

  return useMemo(() => accessFromRole(role, loading), [role, loading]);
}
